/**
 * A percentage as stars, for a child.
 *
 * The score saved in the database stays the exact percentage: teachers and
 * statistics need to tell a 60% from an 85%. This is display only, and only
 * on the student's own reading screen, where a number reads as a verdict.
 *
 * The curve is deliberately generous. A reading that got most of the way
 * through should look like an achievement, because the point is that the
 * child reads again tomorrow. Nobody who finished a reading is shown an empty
 * row of stars.
 */
export const MAX_STARS = 5;

const CURVE: ReadonlyArray<{ from: number; stars: number }> = [
  { from: 90, stars: 5 },
  { from: 75, stars: 4.5 },
  { from: 60, stars: 4 },
  { from: 45, stars: 3.5 },
  { from: 30, stars: 3 },
  { from: 0, stars: 2.5 },
];

/** Stars for a percentage, in halves, between 2.5 and 5. */
export function scoreToStars(percentage: number): number {
  // A number that is not a number would otherwise fall through to the bottom
  // of the curve and quietly look like a bad reading.
  if (!Number.isFinite(percentage)) return 0;
  const clamped = Math.min(Math.max(percentage, 0), 100);
  return (CURVE.find((step) => clamped >= step.from) as { stars: number }).stars;
}

/**
 * The stars split into what to draw: full, half, empty.
 *
 * Returned rather than computed in the component so the arithmetic is testable
 * and the component only places icons.
 */
export function starParts(percentage: number): { full: number; half: number; empty: number } {
  const stars = scoreToStars(percentage);
  const full = Math.floor(stars);
  const half = stars % 1 === 0 ? 0 : 1;
  return { full, half, empty: MAX_STARS - full - half };
}
